import React, { useState } from "react";
import { Checkbox, IconButton } from "@mui/material";
import { Table, Column, AutoSizer } from "react-virtualized";
import "react-virtualized/styles.css"; // Default styles (can be overridden)
import { useMaterialUIController } from "context";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";

// Assuming you have the darkMode state from context
export const VirtualizedTableData = ({
  initialRowIndex,
  isSelection,
  isMultiSelect,
  selectedAll,
  selectedRows,
  setSelectedRows,
  setSelectedAll,
  memoizedRows,
  memoizedColumns,
  setInitialRowIndex,
  setCurrentRowIndex,
  sorting,
  onSortChange,
}) => {
  selectedRows ??= [];

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const handleSort = ({ sortBy, sortDirection }) => {
    const column = memoizedColumns.find((col) => col.accessor === sortBy);
    if (sortBy !== "checkbox" && !column?.disableSortBy) {
      onSortChange(sortBy, sortDirection);
    }
  };

  const toggleRowSelection = (rowId) => {
    if (typeof setSelectedRows !== "function") return;
    setSelectedRows((prevSelectedRows) => {
      const rowSet = new Set(prevSelectedRows);
      rowSet.has(rowId) ? rowSet.delete(rowId) : rowSet.add(rowId);
      return Array.from(rowSet);
    });
  };

  const toggleSelectAll = (selectedAll) => {
    setSelectedRows(selectedAll ? memoizedRows.map((row) => row.id) : []);
    setSelectedAll(selectedAll);
  };

  const handleSelectRowBetween = (index) => {
    let diff = initialRowIndex - index;
    let selected;
    if (diff < 0) {
      selected = memoizedRows.slice(initialRowIndex, index + 1);
    } else {
      selected = memoizedRows.slice(index, initialRowIndex + 1);
    }
    setSelectedRows((previousSelected) => [
      ...new Set([
        ...previousSelected,
        ...selected?.map((select) => select.id),
      ]),
    ]);
  };

  const handleCopy = (event, text) => {
    event.stopPropagation();
    navigator.clipboard.writeText(text);
    toast.info("Copy to Clipboard!")
  };

  const openPage = (event, url) => {
    event.stopPropagation();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflowX: "auto",
        overflowY: "hidden",
      }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <Table
            width={Math.max(
              width,
              memoizedColumns.reduce((sum, col) => sum + col.width, 0)
            ) + 50}
            height={height}
            headerHeight={60}
            rowHeight={50}
            rowCount={memoizedRows.length}
            rowGetter={({ index }) => memoizedRows[index]}
            sort={handleSort}
            sortBy={sorting.orderBy}
            sortDirection={sorting.orderDirection}
            rowStyle={({ index }) => {
              const row = memoizedRows[index];
              const isSelected = row && selectedRows.includes(row.id);

              return {
                cursor: "pointer",
                borderBottom: "0.0625rem solid #f0f2f566",
                backgroundColor:
                  selectedRows && isMultiSelect && isSelected
                    ? "rgba(0, 0, 255, 0.2)"
                    : "", // Default background color
              };
            }}
            onRowClick={({ event, rowData, index }) => {
              if (!event.target.closest("input[type='checkbox']")) {
                toggleRowSelection(rowData.id);
                if (selectedRows?.length <= 0) {
                  setInitialRowIndex(index);
                  setCurrentRowIndex(index);
                } else if (event.shiftKey) {
                  handleSelectRowBetween(index);
                }
              }
            }}
          >
            {/* Selection Column */}
            {isSelection && isMultiSelect && (
              <Column
                label={
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < memoizedRows.length
                    }
                    checked={selectedAll}
                    onChange={() => toggleSelectAll(!selectedAll)}
                  />
                }
                dataKey="checkbox"
                cellRenderer={({ rowData, rowIndex }) => (
                  <Checkbox
                    checked={
                      Array.isArray(selectedRows) && isMultiSelect
                        ? selectedRows.includes(rowData.id)
                        : selectedRows == rowData.id
                    }
                    onClick={(e) => {
                      toggleRowSelection(rowData.id);
                      if (selectedRows.length <= 0) {
                        setInitialRowIndex(rowIndex);
                        setCurrentRowIndex(rowIndex);
                      } else if (e.shiftKey) {
                        handleSelectRowBetween(rowIndex);
                      }
                    }}
                  />
                )}
                flexShrink={0}
                width={70}
                headerStyle={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "0 16px",
                  backgroundColor: darkMode ? "#52566D" : "#52566D",
                  margin: 0,
                  opacity: 0.7,
                  userSelect: "none",
                }}
                style={{
                  textAlign: "center",
                  verticalAlign: "middle",
                  margin: 0,
                }}
              />
            )}

            {/* All Data Columns */}
            {memoizedColumns.map((col) => (
              <Column
                key={col.accessor}
                label={col.Header}
                dataKey={col.accessor}
                cellRenderer={({ rowData }) => {
                  const isRedirect = col.isRedirect;
                  const isCopy = col.isCopy;
                  const cellValue = rowData[col.accessor];

                  if (col.Cell) {
                    return col.Cell({ row: rowData });
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        width: "100%",
                      }}
                      title={cellValue}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          flexGrow: 1,
                        }}
                        onClick={(e) => handleCopy(e, cellValue)}
                      >
                        {cellValue || ""}
                      </span>

                      {isRedirect && cellValue && (
                        <IconButton
                          onClick={(e) => openPage(e, cellValue)}
                          title="Redirect to link"
                          style={{ marginLeft: 8, flexShrink: 0 }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}

                      {/* {isCopy && cellValue && (
                        <IconButton
                          onClick={(e) => handleCopy(e, cellValue)}
                          title="Copy to clipboard"
                          style={{ marginLeft: 8, flexShrink: 0 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      )} */}
                    </div>
                  );
                }}
                width={col.width}
                flexGrow={col.grow ? 1 : 0}
                flexShrink={col.notShrink ? 0 : 1}
                headerStyle={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "0 24px",
                  backgroundColor: darkMode ? "#52566D" : "#52566D",
                  margin: 0,
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "white",
                  opacity: 0.7,
                  userSelect: "none",
                }}
                style={{
                  textAlign: col.align || "left",
                  padding: "0 24px",
                  margin: 0,
                  fontSize: "13.5px",
                  fontWeight: "lighter",
                  color: "white",
                  whiteSpace: "nowrap",
                }}
              />
            ))}
          </Table>
        )}
      </AutoSizer>
    </div>
  );
};
